import React from 'react';

interface SparklineProps {
  data: (number | null)[];
  width?: number;
  height?: number;
  color?: string;
  strokeWidth?: number;
  /**
   * Human-readable name of what the sparkline represents (e.g. "Battery").
   * Combined with first/last values to form an aria-label that AT users
   * can actually act on, instead of the tooltip-only "Battery (last 14d)"
   * that conveyed no trend information. UX-053.
   */
  ariaLabel?: string;
  /** Optional unit string (e.g. "%" or "dBm") appended to the trend. */
  unit?: string;
}

const Sparkline: React.FC<SparklineProps> = ({
  data,
  width = 40,
  height = 16,
  color = '#006EB7',
  strokeWidth = 1.5,
  ariaLabel,
  unit = '',
}) => {
  const valid = data.filter((d): d is number => d != null);
  if (valid.length < 2) return <span style={{ width, display: 'inline-block' }} />;

  const min = Math.min(...valid);
  const max = Math.max(...valid);
  const range = max - min || 1;
  const padding = 1;

  const points = data
    .map((v, i) => {
      if (v == null) return null;
      const x = padding + (i / (data.length - 1)) * (width - 2 * padding);
      const y = padding + (1 - (v - min) / range) * (height - 2 * padding);
      return `${x},${y}`;
    })
    .filter(Boolean)
    .join(' ');

  const first = valid[0];
  const last = valid[valid.length - 1];
  const trend = ariaLabel
    ? `${ariaLabel}: ${first}${unit} → ${last}${unit} (min ${min}${unit}, max ${max}${unit})`
    : `Trend ${first}${unit} → ${last}${unit}`;

  return (
    <svg
      width={width}
      height={height}
      style={{ verticalAlign: 'middle' }}
      role="img"
      aria-label={trend}
    >
      <title>{trend}</title>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default Sparkline;
